import {useEffect, useRef, useState } from 'react';
import { useCredentialing } from '../../Tabs/Credentialing/CredentialingContext';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment';

import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { type LiabilityInsurance as ILiabilityInsurance, type RenewInsuranceHistoryState } from '../../../interfaces';
import { KebabMenu, Spinner } from 'components/Shared';
import { Card } from 'components/Shared/Card';
import { confirm } from 'components/Shared/ConfirmDialog';
import { Button } from 'components/Shared/Inputs';
import { LiabilityInsuranceModal } from './LiabilityInsuranceModal';
import { useAppConfig } from 'providers';
import { useHistoryState } from 'hooks/useHistoryState';
import { useDeleteInsurance } from 'api/queries/credentialsQuery';

interface LiabilityInsuranceProps {
    providerId: number,
    onUpdated: (liabilityInsurance: ILiabilityInsurance) => void
}

const MAX_UPLOADS_ALLOWED = 10;

export const LiabilityInsurance = (props: LiabilityInsuranceProps) => {
    const { providerId, onUpdated } = props;
    const { REACT_APP_FILEUPLOAD_HOST } = useAppConfig();
    const { liabilityInsurance, refreshCredentialing } = useCredentialing();

    const [historyState, setHistoryState] = useHistoryState<RenewInsuranceHistoryState>();
    
    const { t, ready: translationsReady } = useTranslation(['providerprofile', 'fileupload'], { useSuspense: false });

    const [cachePendingUpdate, setCachePendingUpdate] = useState(false);
    const [inEdit, setInEdit] = useState(historyState?.renewInsurance ?? false);
    const [viewMode, setViewMode] = useState<'new'|'renew'|'upload-only'>(historyState?.renewInsurance ? 'renew' : 'new');

    const timeoutRef = useRef<number|null>(null);
    const clearTimeoutRef = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
    }

    const refreshAttachments = () => {
        setCachePendingUpdate(true);
        // introduced a 2-second delay because the API doesn't return the latest upload if we run a search immediately after upload
        timeoutRef.current = window.setTimeout(
            () => {
                refreshCredentialing();
                clearTimeoutRef();
            },
            2000
        );
    }
    
    const { isPending, mutateAsync: deleteInsurance, error: deleteError } = useDeleteInsurance({
        onSuccess: (_, variables) => handleDeleteSuccess(variables),
        onError: (error, variables) => handleDeleteError(error, variables)
    });

    const handleDeleteSuccess = (providerIdInput?: string) => {
        setCachePendingUpdate(true);
        onUpdated({ onFile: false, expiryDate: '' });
        console.info(`Liablity insurance for provider ${providerIdInput} deleted successfully!`);
    };

    const handleDeleteError = (error, providerIdInput?: string) => {
        console.error(`Liability insurance for ${providerIdInput} could not be deleted!`, error, providerIdInput);
    }

    useEffect(() => clearTimeoutRef, []);
    
    useEffect(() => {
        setCachePendingUpdate(false);
    }, [liabilityInsurance?.attachments?.length, liabilityInsurance?.attachments?.[0]?.fileName]);

    useEffect(() => {
        if (historyState?.renewInsurance) {
            if (!inEdit) {
                setInEdit(true);
                setViewMode('renew');
            }
            setHistoryState({ renewInsurance: false })
        }
    }, [historyState?.renewInsurance]);

    const { createDateFormatFunction, DATE_PORTION_MOMENT_FORMAT } = useTimezoneUtils();
    const formatCredentialDate = createDateFormatFunction(DATE_PORTION_MOMENT_FORMAT);

    const expiryDate = liabilityInsurance?.expiryDate ? moment(liabilityInsurance.expiryDate) : null;

    const handleUpload = () => {
        setViewMode('upload-only');
        setInEdit(true);
    };

    const handleAddEntry = () => {
        setViewMode('new');
        setInEdit(true);
    };

    const handleDelete = async () => {
        const dialogProps = {
            title: t('liability_insurance__delete'),
            message: t('liability_insurance__confirm_delete'),
            confirmLabel: t('licenses_registrations__yes_delete')
        };
        if (await confirm(dialogProps)) {
            setCachePendingUpdate(true)
            await deleteInsurance(providerId.toString());
            if (!deleteError) {
                refreshAttachments();
            }
        }
    };

    const handleRenew = () => {
        setViewMode('renew');
        setInEdit(true);
    };
    
    const handleDownload = (fileId: string) => {
        window.open(`${REACT_APP_FILEUPLOAD_HOST}/v1/file/${fileId}/download`, '_blank', 'noreferrer');
    };

    const { setLiabilityInsuranceHandleRenew } = useCredentialing();

    useEffect(() => {
        setLiabilityInsuranceHandleRenew(handleRenew);
        return () => setLiabilityInsuranceHandleRenew(null);
    }, [setLiabilityInsuranceHandleRenew]);

    return !translationsReady
        ? <></>
        : <Card elevation={1} radius="regular" className="provider_profile_section_container">
                {
                    (isPending || cachePendingUpdate) &&
                    <Spinner fillParent backdrop/>
                }
                <div className="provider_profile_section_header">
                    <h2>{t('liability_insurance__section_title')}</h2>
                    {liabilityInsurance?.onFile && <KebabMenu
                        getMenuItems={() => ([
                            { label: t('common:renew'), onClick: handleRenew, hidden: !liabilityInsurance?.onFile },
                            { label: t('licenses_registrations__add_document'), onClick: handleUpload, hidden: (liabilityInsurance?.attachments?.length ?? 0) >= MAX_UPLOADS_ALLOWED },
                            { label: t('common:delete'), onClick: handleDelete, hidden: !liabilityInsurance?.onFile }
                        ])}
                    />}
                </div>
                <Grid container className='liability_insurance_form'>
                    <Grid item xs={12} sm={12}>
                        <p className='global__paragraph liability_expiry_date'>
                        {
                            expiryDate
                            ? expiryDate.isAfter(moment())
                                ? t('liability_insurance__not_expired_description', { replace: { date: formatCredentialDate(expiryDate) }})
                                : t('liability_insurance__expired_description', { replace: { date: formatCredentialDate(expiryDate) }})
                            : t('liability_insurance__not_available')
                        }
                        </p>
                        {!expiryDate && (
                            <p>
                                <Button variant="text" onClick={handleAddEntry} className="add_entry_button">
                                    <AddIcon />{t('common__add_entry_button_label')}
                                </Button>
                            </p>
                        )}
                    </Grid> 
                    {!!expiryDate && liabilityInsurance?.attachments?.map(({ fileId, fileName, createdOn }) =>(
                        <div className="attachments_wrapper" key={fileId}>
                            <div className="attachment_section">
                                <div className="file_icon">
                                    <UploadFileIcon className="file-icon" />
                                </div>
                                <div className="attachment_present">
                                    <p className='global__paragraph'><a href={`${REACT_APP_FILEUPLOAD_HOST}/v1/file/${fileId}/preview`} target="_blank" rel="noreferrer">{fileName}</a></p>
                                    <p className='global__paragraph--small'>{t('licenses_registrations__uploaded')} {moment(createdOn).format('ddd ll')}</p>
                                </div>
                                
                                <div className="download_section">
                                    <Button variant="text" onClick={() => handleDownload(fileId)}><DownloadIcon />&nbsp;{t('fileupload:download')}</Button>
                                </div>
                            </div>
                        </div>
                ))}
                {!!expiryDate && !liabilityInsurance?.attachments?.length && (
                    <div className="attachments_wrapper">
                        <div className="attachment_section">
                            <div className="file_icon">
                                <UploadFileIcon className="file-icon" />
                            </div>
                            <div className="attachment_absent">
                                <p className='global__paragraph'>{t('licenses_registrations__no_docs_found')}</p>
                                <p className='action_button_segment'><Button onClick={handleUpload}>{t('licenses_registrations__upload_doc')}</Button></p>
                            </div>
                        </div>
                    </div>
                )}
                {(liabilityInsurance?.attachments?.length ?? 0) >= MAX_UPLOADS_ALLOWED && (
                    <Alert className="upload_limit_warning" variant="standard" color="info" icon={<InfoIcon color="primary" />}>
                        {t('licenses_registrations__max_upload_reached')}
                    </Alert>
                )}
                </Grid>
            <LiabilityInsuranceModal
                viewMode={viewMode}
                open={inEdit}
                cachePendingUpdate={cachePendingUpdate}
                setCachePendingUpdate={setCachePendingUpdate}
                setModalOpen={setInEdit}
                providerId={providerId}
                onUpdated={onUpdated}
                liabilityInsurance={liabilityInsurance}
                refreshAttachments={refreshAttachments}
            />
        </Card>
};
